import { useState } from 'react';
import { Mail } from 'lucide-react';
import { HeaderKeyControl } from '@/components/HeaderKeyControl';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { Button } from '@/components/ui/button';
import { InviteAcceptModal } from '@/components/InviteAcceptModal';
import {
  usePersonalApiKeyStatus,
  useGeneratePersonalApiKey,
} from '@/hooks/api/useApiKeys';
import { useOrganizations } from '@/hooks/api/useOrganizations';

/**
 * Personal API Key Control Component
 *
 * Wraps HeaderKeyControl with personal API key functionality.
 * Handles personal API key status and generation.
 *
 * While the user has an open organization invite, personal accounts are locked
 * out (KIR-3786): generation is disabled here and the backend also returns 403.
 * A banner explains why the key is unavailable and lets the user accept the
 * invite inline (the org switcher also surfaces the same pending invite), so the
 * lock is never silent.
 */
export function PersonalKeyControl() {
  const statusQuery = usePersonalApiKeyStatus();
  const generateMutation = useGeneratePersonalApiKey();
  const { data: organizations = [] } = useOrganizations();
  const [showInvite, setShowInvite] = useState(false);

  const invitedOrg = organizations.find(org => org.isInvited);
  const hasOpenInvite = Boolean(invitedOrg);

  return (
    <div className="flex flex-col gap-2">
      {invitedOrg && (
        <HighlightBox
          variant="warning"
          radius="md"
          className="flex items-center gap-2 text-sm"
          role="alert"
        >
          <Mail className="h-4 w-4 flex-shrink-0" />
          <span className="min-w-0 flex-1">
            You have a pending invitation to{' '}
            <span className="font-semibold">{invitedOrg.name}</span>. Accept it
            to continue — personal API keys are unavailable while an invite is
            open.
          </span>
          <Button
            size="sm"
            className="h-7 flex-shrink-0"
            onClick={() => setShowInvite(true)}
          >
            Accept invitation
          </Button>
        </HighlightBox>
      )}

      <HeaderKeyControl
        statusQuery={statusQuery}
        generateMutation={generateMutation}
        disableGenerate={hasOpenInvite}
      />

      {invitedOrg && (
        <InviteAcceptModal
          open={showInvite}
          onOpenChange={setShowInvite}
          organizationId={invitedOrg.id}
          organizationName={invitedOrg.name}
        />
      )}
    </div>
  );
}
